/**
 * useTraitMutation
 *
 * The single hook used by every platform feature that can affect traits:
 *  - micro-course quizzes
 *  - game completions
 *  - activity log entries
 *  - exam result entries
 *
 * Usage:
 *   const { emitQuizResult, emitGameResult, emitActivityLog, emitExamResult } = useTraitMutation();
 *
 * Each emitter:
 *  1. Builds a TraitEvent
 *  2. Applies it to the student's current traitProfile via the mutation engine
 *  3. Appends the event to the student's traitEvents log
 *  4. Persists everything to StorageService
 *  5. Returns the updated traitProfile so the caller can show immediate UI feedback
 */

import { useCallback } from 'react';
import { StorageService } from '@/hooks/services/storageService';
import { AuthStore } from '@/hooks/stores/AuthStore';
import { goalEngine } from '@/hooks/useGoalEngine';
import {
  TraitEvent,
  QuizResult,
  GameResult,
  ActivityLogEntry,
  ActivityLogType,
  OutcomeRating,
  ACTIVITY_LOG_META,
  OUTCOME_META,
} from '@/types/traitEvents';
import {
  applyTraitEvent,
  gameResultToDeltas,
  activityLogToDeltas,
} from '@/utils/traitMutator';
import { TraitProfile } from '@/utils/traitScoring';
import { runCareerEngine } from '../../Career Suggestion Engine/index';
import type { StudentProfile, ActivityResult, ActivityTrait, GeneralTrait } from '../../Career Suggestion Engine/types';
import { CAREER_TREE } from '@/data/career-tree';
import traitList from '../../traitList.json';

// ─── CSE bridge helpers ───────────────────────────────────────────────────────

const TRAIT_NAMES: Record<string, string> = Object.fromEntries(
  (traitList as Array<{ id: string; full_name: string }>).map((t) => [t.id, t.full_name])
);

const SOURCE_TO_ACTIVITY_TYPE: Record<string, 'game' | 'quiz' | 'test' | 'micro_lesson'> = {
  game:          'game',
  quiz:          'quiz',
  exam_result:   'test',
  activity_log:  'test',
  course:        'micro_lesson',
  psychometric:  'quiz',
};

/** Build a CSE StudentProfile from our flat student record */
function buildStudentProfile(student: any): StudentProfile {
  const generalTraits: GeneralTrait[] = student.generalTraits?.length
    ? student.generalTraits
    : (traitList as Array<{ id: string; full_name: string }>).map((t) => ({
        traitId: t.id,
        name: t.full_name,
        score: student.traitProfile?.[t.id] ?? 0,
        updateCount: 0,
      }));

  return {
    studentId: student._id,
    hasCompletedPsychometricTest: student.traitProfile !== null,
    suggestionScope: student.suggestionScope ?? 'declared',
    generalTraits,
    activeNodes: student.activeNodes ?? [],
  };
}

/** Convert a TraitEvent into a CSE ActivityResult */
function traitEventToActivityResult(event: TraitEvent, updatedProfile: TraitProfile): ActivityResult {
  const activityType = SOURCE_TO_ACTIVITY_TYPE[event.source] ?? 'quiz';
  const traitResults: ActivityTrait[] = Object.entries(event.traitDeltas).map(([traitId]) => ({
    traitId,
    name: TRAIT_NAMES[traitId] ?? traitId,
    score: updatedProfile[traitId] ?? 0,
  }));

  return {
    activityId: event.id,
    activityType,
    studentId: event.studentId,
    careerPathNodeId: null, // general activity — affects all matching active nodes
    traitResults,
    completedAt: new Date(event.timestamp),
  };
}

// ─────────────────────────────────────────────────────────────────────────────

function makeId(): string {
  return `evt_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

function now(): string {
  return new Date().toISOString();
}

/** Read the current student record, returning { student, index, students } or null */
function getStudentRecord(currentUser: any): {
  student: any;
  index: number;
  students: any[];
} | null {
  const students: any[] = StorageService.read('students');
  const index = students.findIndex(
    (s) => s.userId === currentUser._id || s.email === currentUser.email
  );
  if (index === -1) return null;
  return { student: students[index], index, students };
}

/** Persist the mutated student record */
function persist(students: any[], index: number, updated: any): void {
  students[index] = { ...updated, updatedAt: new Date() };
  StorageService.write('students', students);
}

// ─────────────────────────────────────────────────────────────────────────────

export function useTraitMutation() {
  const currentUser = AuthStore.getState().currentUser;

  /** Apply an event and save. Returns updated traitProfile or null on failure. */
  const applyEvent = useCallback(
    (event: TraitEvent): TraitProfile | null => {
      if (!currentUser) return null;
      const rec = getStudentRecord(currentUser);
      if (!rec) return null;

      const { student, index, students } = rec;
      const currentProfile: TraitProfile = student.traitProfile ?? {};
      const updatedProfile = applyTraitEvent(currentProfile, event);
      const traitEvents: TraitEvent[] = student.traitEvents ?? [];

      // Only run CSE if the student has active nodes (broad paths chosen)
      let cseUpdates: { generalTraits?: any[]; activeNodes?: any[]; careerSuggestions?: any[] } = {};
      if (student.activeNodes?.length > 0) {
        try {
          const studentProfile = buildStudentProfile({ ...student, traitProfile: updatedProfile });
          const activityResult = traitEventToActivityResult(event, updatedProfile);
          const cseOutput = runCareerEngine({ student: studentProfile, activityResult, careerTree: CAREER_TREE });
          cseUpdates = {
            generalTraits: cseOutput.mutation.updatedGeneralTraits,
            activeNodes: cseOutput.mutation.updatedActiveNodes,
            careerSuggestions: cseOutput.suggestions.suggestions,
          };
        } catch (e) {
          // CSE failure is non-fatal — trait profile still saved
          console.warn('CSE run failed:', e);
        }
      }

      const updatedStudent = {
        ...student,
        traitProfile: updatedProfile,
        traitEvents: [...traitEvents, event],
        ...cseUpdates,
      };
      persist(students, index, updatedStudent);

      // Notify goal engine of trait changes
      for (const [traitId, delta] of Object.entries(event.traitDeltas)) {
        const newScore = updatedProfile[traitId] ?? 0;
        if (delta !== 0) goalEngine.onTraitUpdated(traitId, newScore);
      }
      goalEngine.checkAchievements(updatedStudent);

      return updatedProfile;
    },
    [currentUser]
  );

  // ─── Emitters ─────────────────────────────────────────────────────────────

  /** Call after a student answers a single quiz question */
  const emitQuizResult = useCallback(
    (result: QuizResult): TraitProfile | null => {
      const event: TraitEvent = {
        id: makeId(),
        studentId: currentUser?._id ?? '',
        source: 'quiz',
        sourceId: result.questionId,
        sourceName: result.courseName,
        traitDeltas: result.rawTraitDeltas,
        timestamp: now(),
        meta: {
          timeTakenSeconds: result.timeTakenSeconds,
          timeLimitSeconds: result.timeLimitSeconds,
          correct: result.correct,
          label: `${result.courseName} — Quiz`,
        },
      };
      return applyEvent(event);
    },
    [applyEvent, currentUser]
  );

  /** Call when a student completes an entire course module */
  const emitCourseCompletion = useCallback(
    (courseId: string, courseName: string, score: number, difficulty: 1 | 2 | 3, bonusDeltas: Record<string, number>): TraitProfile | null => {
      const event: TraitEvent = {
        id: makeId(),
        studentId: currentUser?._id ?? '',
        source: 'course',
        sourceId: courseId,
        sourceName: courseName,
        traitDeltas: bonusDeltas,
        timestamp: now(),
        meta: { score, difficulty, label: `${courseName} — Completed` },
      };
      return applyEvent(event);
    },
    [applyEvent, currentUser]
  );

  /** Call from game's game-over screen */
  const emitGameResult = useCallback(
    (result: GameResult): TraitProfile | null => {
      const deltas = gameResultToDeltas(result.primaryTraits, result.score, result.maxScore);
      const event: TraitEvent = {
        id: makeId(),
        studentId: currentUser?._id ?? '',
        source: 'game',
        sourceId: result.gameId,
        sourceName: result.gameName,
        traitDeltas: deltas,
        timestamp: now(),
        meta: {
          score: Math.round((result.score / result.maxScore) * 100),
          timeTakenSeconds: result.durationSeconds,
          difficulty: result.difficulty,
          label: result.gameName,
        },
      };
      const updated = applyEvent(event);
      goalEngine.onGamePlayed();
      return updated;
    },
    [applyEvent, currentUser]
  );

  /** Call from the Activity Logger when a student submits a real-world activity */
  const emitActivityLog = useCallback(
    (
      activityType: ActivityLogType,
      subject: string,
      outcomeRating: OutcomeRating,
      actualScore?: number,
      note?: string
    ): { updatedProfile: TraitProfile | null; entry: ActivityLogEntry } => {
      const meta = ACTIVITY_LOG_META[activityType];
      const outcome = OUTCOME_META[outcomeRating];
      const deltas = activityLogToDeltas(meta.coreTraits, outcome.multiplier, actualScore);

      const eventId = makeId();
      const event: TraitEvent = {
        id: eventId,
        studentId: currentUser?._id ?? '',
        source: 'activity_log',
        sourceId: eventId,
        sourceName: `${meta.label}${subject ? ` — ${subject}` : ''}`,
        traitDeltas: deltas,
        timestamp: now(),
        meta: {
          score: actualScore,
          activityType,
          subject,
          note,
          label: meta.label,
        },
      };

      const entry: ActivityLogEntry = {
        id: makeId(),
        activityType,
        subject,
        outcomeRating,
        score: actualScore,
        note,
        timestamp: now(),
        traitEventId: eventId,
      };

      // Persist activity log entry alongside the event
      if (currentUser) {
        const rec = getStudentRecord(currentUser);
        if (rec) {
          const { student, index, students } = rec;
          const activityLog: ActivityLogEntry[] = student.activityLog ?? [];
          const currentProfile: TraitProfile = student.traitProfile ?? {};
          const updatedProfile = applyTraitEvent(currentProfile, event);
          const traitEvents: TraitEvent[] = student.traitEvents ?? [];

          let cseUpdates: { generalTraits?: any[]; activeNodes?: any[]; careerSuggestions?: any[] } = {};
          if (student.activeNodes?.length > 0) {
            try {
              const studentProfile = buildStudentProfile({ ...student, traitProfile: updatedProfile });
              const activityResult = traitEventToActivityResult(event, updatedProfile);
              const cseOutput = runCareerEngine({ student: studentProfile, activityResult, careerTree: CAREER_TREE });
              cseUpdates = {
                generalTraits: cseOutput.mutation.updatedGeneralTraits,
                activeNodes: cseOutput.mutation.updatedActiveNodes,
                careerSuggestions: cseOutput.suggestions.suggestions,
              };
            } catch (e) {
              console.warn('CSE run failed:', e);
            }
          }

          persist(students, index, {
            ...student,
            traitProfile: updatedProfile,
            traitEvents: [...traitEvents, event],
            activityLog: [entry, ...activityLog], // newest first
            ...cseUpdates,
          });

          goalEngine.onActivityLogged();
          for (const [traitId, delta] of Object.entries(event.traitDeltas)) {
            const newScore = updatedProfile[traitId] ?? 0;
            if (delta !== 0) goalEngine.onTraitUpdated(traitId, newScore);
          }
          goalEngine.checkAchievements({ ...student, traitProfile: updatedProfile, activityLog: [entry, ...activityLog] });

          return { updatedProfile, entry };
        }
      }

      return { updatedProfile: null, entry };
    },
    [currentUser]
  );

  /** Call when student enters a terminal exam / external result */
  const emitExamResult = useCallback(
    (
      examName: string,
      subject: string,
      score: number,
      relatedTraits: string[]
    ): TraitProfile | null => {
      const deltas = Object.fromEntries(
        relatedTraits.map((t) => [t, 3]) // base delta — score modifier handles the rest
      );
      const event: TraitEvent = {
        id: makeId(),
        studentId: currentUser?._id ?? '',
        source: 'exam_result',
        sourceId: makeId(),
        sourceName: examName,
        traitDeltas: deltas,
        timestamp: now(),
        meta: { score, subject, label: `${examName} — ${subject}` },
      };
      return applyEvent(event);
    },
    [applyEvent, currentUser]
  );

  /**
   * Emit a lightweight interest signal when a student browses or engages with
   * career-adjacent content (e.g. opening a career profile, reading about a path).
   * Does NOT create a TraitEvent but does log a platform activity entry.
   */
  const emitInterestSignal = useCallback(
    (subjectArea: string, careerPathId?: string): void => {
      if (!currentUser) return
      const rec = getStudentRecord(currentUser)
      if (!rec) return

      const { student, index, students } = rec
      const signals: Array<{ subjectArea: string; careerPathId?: string; timestamp: string }> =
        student.interestSignals ?? []
      students[index] = {
        ...student,
        interestSignals: [
          { subjectArea, careerPathId, timestamp: now() },
          ...signals,
        ].slice(0, 200),
      }
      StorageService.write('students', students)
    },
    [currentUser]
  )

  return {
    emitQuizResult,
    emitCourseCompletion,
    emitGameResult,
    emitActivityLog,
    emitExamResult,
    emitInterestSignal,
  };
}
